/*
 Welder Record Sheet Component:
 1. Generates welder record sheets showing welders qualified for specific WPS/processes
 2. Groups data by joint numbers and shows unique WPS-Process-Welder combinations
 3. Supports both Excel and PDF export formats with custom formatting
 4. Eliminates duplicate entries and consolidates welder information per WPS

 Backend Services Used:
 - WIexportreport: Queries WI_HEADER table with filters to get welding instruction data for record sheet generation
 - getListofProjectno: Gets all project numbers with joint numbers from PROJECT_NO table for dropdown population
 - getWIdataexcelbyProjectno: Gets WI data for specific project+joint combination to populate welder dropdown

 Database Tables:
 - WI_HEADER: Welding instruction header table (PK: "WI_HEADER") with welder, WPS, and process information
 - PROJECT_NO: Project master table (PK: "PROJECT_NO", SK: project_number) with JointNumbers field

 Key Features:
 - Data deduplication and grouping by Joint → WPS → Process → Welders
 - Multiple export formats (Excel with custom styling, PDF with images)
 - Cascading dropdown filters (Project → Joint → Welder)
 - Process category extraction from WI_ProcessCategory field (splits by "#" delimiter)
 - Welder consolidation per WPS (multiple welders shown as comma-separated list)
 - Custom PDF generation with company branding and formatted tables
*/
import React, { useState, forwardRef, useEffect } from "react";
import { Row, Col, Container, Button } from "react-bootstrap";
import { showWarning, showInfo, showSuccess } from "../utility/ui/notifications/Toaster";
import { WIexportreport } from "../../Services/WI-services";
import { exportWelderRecordToExcel } from "../utility/data/export/ExcelUtils";
import jsPDF from "jspdf";
import images from "../../icons/welder report.png";
import Godrej_Logo from "../../icons/Godrej_Logo.png";
import { useWelderReportCommon } from "../../hooks/useWelderReportCommon";
import WelderReportFilters from "../utility/forms/WelderReportFilters";
import { validateWelderReportForm } from "../../utils/welderReportValidation";
import { GridLoader } from "react-spinners";

const Welder_Record_sheet = forwardRef((props, ref) => {
  const {
    wiobj, setwiobj, error, setError, Slipstatus,
    getUniqueProjectNumbers, getJointNosForSelectedProject, getWelderForSelectedProject,
    handleProjectChange, handleJointChange, handleWelderChange, handleSlipStatusChange
  } = useWelderReportCommon();

  const [imageBase64, setImageBase64] = useState("");
  const [loading, setLoading] = useState(false);

  const fileformat = [
    { value: "Excel", name: "Excel" },
    { value: "PDF", name: "PDF" },
  ];



  useEffect(() => {
    const fetchImage = async () => {
      try {
        const response = await fetch(images);
        const blob = await response.blob();
        const reader = new FileReader();

        reader.onload = () => {
          const base64String = reader.result;
          setImageBase64(base64String);
        };

        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Error fetching image:", error);
      }
    };

    fetchImage();
  }, [images]);

  /**
   * WELDER RECORD SHEET DATA STRUCTURE
   * 
   * Purpose: Groups welding data to show which welders are qualified for specific WPS-Process combinations
   * 
   * Input Data (from WI_HEADER table):
   * [
   *   { WI_Joint_No: "J001", WI_WPS_No: "WPS-123", WI_ProcessCategory: "GTAW#TIG#Manual", WI_Welder_Id: "W001" },
   *   { WI_Joint_No: "J001", WI_WPS_No: "WPS-123", WI_ProcessCategory: "GTAW#TIG#Manual", WI_Welder_Id: "W002" },
   *   { WI_Joint_No: "J001", WI_WPS_No: "WPS-456", WI_ProcessCategory: "SMAW#Stick#Manual", WI_Welder_Id: "W001" }
   * ]
   * 
   * Final Output Structure:
   * [
   *   {
   *     WI_Joint_No: "J001",
   *     Data: [
   *       {
   *         WI_WPS_No: "WPS-123",
   *         WI_ProcessCategory: "GTAW#TIG#Manual",
   *         WI_Welder_Id: ["W001", "W002"],  // Consolidated welders
   *         WPS_Rev_no: "Rev-1"
   *       },
   *       {
   *         WI_WPS_No: "WPS-456", 
   *         WI_ProcessCategory: "SMAW#Stick#Manual",
   *         WI_Welder_Id: ["W001"],
   *         WPS_Rev_no: "Rev-2"
   *       }
   *     ]
   *   }
   * ]
   * 
   * Report Layout:
   * | Sr No | Joint No | WPS No  | Rev No | Process | Welder IDs    |
   * |-------|----------|---------|--------|---------|---------------|
   * | 1     | J001     | WPS-123 | Rev-1  | Manual  | W001, W002    |
   * |       |          | WPS-456 | Rev-2  | Manual  | W001          |
   */


  /**
   * Optimized data fetching with chunked processing for large datasets (100K+ records)
   * Handles memory management and provides progress feedback
   */
  // Paginated data fetching for large datasets
  const fetchAndProcessDataPaginated = async () => {
    const BATCH_SIZE = 1000;
    let allData = [];
    let lastKey = null;
    let hasMore = true;
    let batchCount = 0;

    console.log(`🚀 Starting paginated data fetch for Welder Record Sheet`);

    while (hasMore) {
      try {
        const batch = await WIexportreport(wiobj, {
          paginated: true,
          limit: BATCH_SIZE,
          lastKey: lastKey
        });

        allData = [...allData, ...batch.items];
        lastKey = batch.lastKey;
        hasMore = batch.hasMore;
        batchCount++;

        console.log(`📦 Batch ${batchCount}: ${batch.items.length} records (Total: ${allData.length})`);

        // Test delay between batches (remove in production)
        await new Promise(resolve => setTimeout(resolve, 1000));

      } catch (error) {
        console.error(`❌ Error in batch ${batchCount}:`, error);
        throw error;
      }
    }

    console.log(`✅ Paginated fetch completed: ${allData.length} total records in ${batchCount} batches`);
    return allData;
  };

  const fetchAndProcessData = async () => {
    // Estimate if we need pagination based on filters
    // Force pagination for testing (remove in production)
    const needsPagination = true; // !wiobj.WI_Joint_No && !wiobj.WI_Welder_Id;

    let result;
    if (needsPagination) {
      console.log(`📄 Using paginated fetch (no specific joint/welder filter)`);
      result = await fetchAndProcessDataPaginated();
    } else {
      console.log(`📄 Using standard fetch (specific filters applied)`);
      result = await WIexportreport(wiobj);
    }


    /**
     * STEP 1: Group by composite key (Joint|WPS|Process)
     * 
     * Input (result from API):
     * [
     *   { WI_Joint_No: "J001", WI_WPS_No: "WPS-123", WI_ProcessCategory: "GTAW#TIG#Manual", WI_Welder_Id: "W001" },
     *   { WI_Joint_No: "J001", WI_WPS_No: "WPS-123", WI_ProcessCategory: "GTAW#TIG#Manual", WI_Welder_Id: "W002" },
     *   { WI_Joint_No: "J001", WI_WPS_No: "WPS-456", WI_ProcessCategory: "SMAW#Stick#Manual", WI_Welder_Id: "W001" },
     *   { WI_Joint_No: "J002", WI_WPS_No: "WPS-789", WI_ProcessCategory: "GTAW#TIG#Auto", WI_Welder_Id: "W003" }
     * ]
     * 
     * Output (groupMap):
     * Map {
     *   "J001|WPS-123|GTAW#TIG#Manual" => { ...item, welders: Set(["W001", "W002"]) },
     *   "J001|WPS-456|SMAW#Stick#Manual" => { ...item, welders: Set(["W001"]) },
     *   "J002|WPS-789|GTAW#TIG#Auto" => { ...item, welders: Set(["W003"]) }
     * }
     */
    const groupMap = new Map();

    result.forEach(item => {
      const key = `${item.WI_Joint_No}|${item.WI_WPS_No}|${item.WI_ProcessCategory}`;
      console.log("groupkey", key, item.WI_ProcessCategory)
      if (groupMap.has(key)) {
        groupMap.get(key).welders.add(item.WI_Welder_Id);
      } else {
        groupMap.set(key, {
          ...item,
          welders: new Set([item.WI_Welder_Id])
        });
      }
    });

    /**
     * STEP 2: Transform to final export format with serial numbering
     * 
     * Input (sortedItems from groupMap):
     * [
     *   { WI_Joint_No: "J001", WI_WPS_No: "WPS-123", WI_ProcessCategory: "GTAW#TIG#Manual", welders: Set(["W001", "W002"]) },
     *   { WI_Joint_No: "J001", WI_WPS_No: "WPS-456", WI_ProcessCategory: "SMAW#Stick#Manual", welders: Set(["W001"]) },
     *   { WI_Joint_No: "J002", WI_WPS_No: "WPS-789", WI_ProcessCategory: "GTAW#TIG#Auto", welders: Set(["W003"]) }
     * ]
     * 
     * Output (transformedData):
     * [
     *   { serialNumber: 1, jointNo: "J001", wpsNo: "WPS-123", revNo: "Rev-1", process: "Manual", welderIds: ["W001", "W002"] },
     *   { serialNumber: null, jointNo: "", wpsNo: "WPS-456", revNo: "Rev-2", process: "Manual", welderIds: ["W001"] },
     *   { serialNumber: 2, jointNo: "J002", wpsNo: "WPS-789", revNo: "Rev-1", process: "Auto", welderIds: ["W003"] }
     * ]
     */

    let serialNumber = 1;
    let prevJointNo = null;
    const transformedData = [];


    // Sort by joint number for proper serial numbering
    const sortedItems = Array.from(groupMap.values()).sort((a, b) =>
      a.WI_Joint_No.localeCompare(b.WI_Joint_No)
    );

    sortedItems.forEach(item => {
      const shouldPrintJointNo = item.WI_Joint_No !== prevJointNo;
      const wldingProcess = item.WI_ProcessCategory?.split("#");
      const category = wldingProcess && wldingProcess[2] ? wldingProcess?.[0] : "";

      transformedData.push({
        serialNumber: shouldPrintJointNo ? serialNumber : null,
        jointNo: shouldPrintJointNo ? item.WI_Joint_No : "",
        wpsNo: item.WI_WPS_No,
        revNo: item.WPS_Rev_no,
        process: category,
        welderIds: Array.from(item.welders),
        isFirstWPSOfJoint: shouldPrintJointNo
      });

      if (shouldPrintJointNo) serialNumber++;
      prevJointNo = item.WI_Joint_No;
    });

    return transformedData;
  };



  /**
   * EXCEL EXPORT CONFIGURATION
   * 
   * Excel Layout Structure:
   * Row 1: [Company Name]                    [Manufacturing]
   * Row 2:                                   [Welder Record Sheet]
   * Row 3:          [Document Number]
   * Row 4:          [Project Info]
   * Row 5: 
   * Row 6: [Sr No] [Joint No] [WPS No] [Rev No] [Process] [Welder IDs]
   * Row 7: [  1  ] [ J001   ] [WPS-123] [Rev-1] [Manual ] [W001, W002]
   * Row 8: [     ] [        ] [WPS-456] [Rev-2] [Manual ] [W001      ]
   * 
   * Column Mapping:
   * A: Serial Number (only for first WPS of joint)
   * B: Joint Number (only for first WPS of joint)
   * C: WPS Number
   * D: WPS Revision
   * E: Process Category (extracted from WI_ProcessCategory)
   * F: Consolidated Welder IDs (comma-separated)
   */
  const exportToExcel = (transformedData) => {
    // Transform data to Excel column format
    const Data = transformedData.map(item => ({
      A: item.serialNumber || "",           // Serial number (only for first WPS of joint)
      B: item.jointNo,                      // Joint number (only for first WPS of joint)
      C: item.wpsNo,                        // WPS number
      D: item.revNo,                        // WPS revision
      E: item.process,                      // Process category
      F: item.welderIds.join(", ")          // Consolidated welder IDs
    }));

    // Excel column configuration
    const width = [
      { col: "A", width: 5.91 },   // Sr No
      { col: "B", width: 19.91 },  // Joint No
      { col: "C", width: 20.91 },  // WPS No
      { col: "D", width: 15.91 },  // Rev No
      { col: "E", width: 15.91 },  // Process
      { col: "F", width: 13.55 },  // Welder IDs
    ];

    // Get project info from first item
    const projectNo = wiobj.WI_Project_No || "N/A";

    // Excel header configuration
    const Headings = [
      { col: "A1", value: "Godrej Process Equipment" },
      { col: "D1", value: "Manufacturing" },
      { col: "D2", value: "Welder Record Sheet" },
      { col: "B3", value: `Document number : PED/WRS/${projectNo}` },
      { col: "B4", value: `Project:${projectNo}` }
    ];

    exportWelderRecordToExcel("Welder Record Sheet", width, transformedData, Data, {
      titleCell: "E3",
      titleWelderRange: "A6:F6",
      tbodyHeadRange: "C1:F2",
      tbodyRange: "A3",
      blankRow: "",
      Headings,
      imageBase64
    });
  };

  /**
   * PDF table header creation - reusable function for initial page and page breaks
   * 
   * Column Positions (X coordinates):
   * - Sr No: 2-15 (width: 13)
   * - Joint No: 17-56 (width: 39) 
   * - WPS No: 57-108 (width: 51)
   * - Rev No: 110-128 (width: 18)
   * - Process: 130-168 (width: 38)
   * - Welder IDs: 170+ (remaining width)
   */
  const createPDFTableHeader = (pdf, headerX, y) => {
    pdf.setFontSize(10);
    pdf.rect(headerX, y, 200, 10); // Main header rectangle

    // Column headers with vertical dividers
    pdf.text("Sr No", headerX + 2, y + 7);
    pdf.line(headerX + 15, y, headerX + 15, y + 10);
    pdf.text("Joint No", headerX + 17, y + 7);
    pdf.line(headerX + 56, y, headerX + 56, y + 10);
    pdf.text("WPS No", headerX + 57, y + 7);
    pdf.line(headerX + 108, y, headerX + 108, y + 10);
    pdf.text("Rev No", headerX + 110, y + 7);
    pdf.line(headerX + 128, y, headerX + 128, y + 10);
    pdf.text("Process", headerX + 130, y + 7);
    pdf.line(headerX + 168, y, headerX + 168, y + 10);
    pdf.text("Welder IDs", headerX + 170, y + 7);
  };

  /**
   * PDF EXPORT CONFIGURATION
   * 
   * Layout Structure:
   * ┌─────────────────────────────────────────────────────────────┐
   * │ [LOGO]              Manufacturing                           │
   * │                     Welder Record Sheet                     │
   * │                                                             │
   * │ Document number: PED/WRS/PROJECT-001                        │
   * │ Project: PROJECT-001                                        │
   * ├─────┬──────────┬──────────┬────────┬─────────┬─────────────┤
   * │Sr No│ Joint No │  WPS No  │ Rev No │ Process │ Welder IDs  │
   * ├─────┼──────────┼──────────┼────────┼─────────┼─────────────┤
   * │  1  │  J001    │ WPS-123  │ Rev-1  │ Manual  │ W001, W002  │
   * │     │          │ WPS-456  │ Rev-2  │ Manual  │ W001        │
   * │  2  │  J002    │ WPS-789  │ Rev-1  │ Auto    │ W003, W004  │
   * └─────┴──────────┴──────────┴────────┴─────────┴─────────────┘
   * 
   * Features:
   * - Auto page breaks when content exceeds page height
   * - Header repeated on each new page
   * - Welder IDs grouped 2 per line for better readability
   * - Dynamic row height based on number of welders
   * - Company logo and branding
   */
  const exportToPDF = async (transformedData) => {
    console.log(`📄 Starting PDF export with ${transformedData.length} records`);

    // Memory warning for large PDF exports
    if (transformedData.length > 5000) {
      console.warn(`⚠️ Large PDF export (${transformedData.length} records). This may take time and use significant memory.`);
    }
    const pdf = new jsPDF();

    // PDF Header Setup - Company branding and document info
    pdf.addImage(Godrej_Logo, "PNG", 10, 15, 20, 9);
    pdf.setFont("GE-BodyCopy", "bolditalic");
    pdf.text("Manufacturing", 100, 20);
    pdf.text("Welder Record Sheet", 90, 30);
    pdf.setFont("GE-BodyCopy", "normal");

    // Document identification
    pdf.setFontSize(12);
    const projectNo = wiobj.WI_Project_No || "N/A";
    pdf.text(`Document number: PED/WRS/${projectNo}`, 20, 45);
    pdf.text(`Project: ${projectNo}`, 20, 55);

    const marginLeft = 5;
    const headerX = marginLeft;
    let y = 70;

    createPDFTableHeader(pdf, headerX, y);
    y = 80;

    const pageHeight = pdf.internal.pageSize.height;

    // Simple PDF generation without chunking
    transformedData.forEach((item) => {
      y = processPDFRow(pdf, item, marginLeft, headerX, y, pageHeight);
    });

    console.log(`✅ PDF generation completed`);
    pdf.save("Welder Record Sheet.pdf");
  };

  // Helper function for PDF row processing
  const processPDFRow = (pdf, item, marginLeft, headerX, y, pageHeight) => {
    // Page break handling
    if (y + 20 > pageHeight) {
      pdf.addPage();
      y = 20;
      createPDFTableHeader(pdf, headerX, y);
      y += 20;
    }

    // Group welder IDs (2 per line for better readability)
    const welderIds = Array.isArray(item.welderIds) ? item.welderIds : [];
    const welderIdGroups = [];
    for (let i = 0; i < welderIds.length; i += 2) {
      welderIdGroups.push(welderIds.slice(i, i + 2).join(", "));
    }

    // Calculate dynamic row height based on number of welder groups
    const rowHeight = Math.max(10, welderIdGroups.length * 10);

    // Draw row border and populate data
    pdf.rect(marginLeft, y, 200, rowHeight);
    pdf.setFontSize(12);

    // Populate table cells with data
    pdf.text(item.serialNumber ? String(item.serialNumber) : "", marginLeft + 2, y + 7);
    pdf.text(String(item.jointNo), marginLeft + 17, y + 7);
    pdf.text(item.wpsNo || "N/A", marginLeft + 57, y + 7);
    pdf.text(item.revNo || "N/A", marginLeft + 110, y + 7);
    pdf.text(item.process || "N/A", marginLeft + 130, y + 7);

    // Draw welder IDs with proper grouping and vertical lines
    welderIdGroups.forEach((welderIdText, groupIndex) => {
      pdf.line(marginLeft + 15, y, marginLeft + 15, y + 10 + groupIndex * 10);
      pdf.line(marginLeft + 56, y, marginLeft + 56, y + 10 + groupIndex * 10);
      pdf.line(marginLeft + 108, y, marginLeft + 108, y + 10 + groupIndex * 10);
      pdf.line(marginLeft + 128, y, marginLeft + 128, y + 10 + groupIndex * 10);
      pdf.line(marginLeft + 168, y, marginLeft + 168, y + 10 + groupIndex * 10);
      pdf.text(welderIdText, marginLeft + 170, y + 7 + groupIndex * 10);
    });

    return y + rowHeight;
  };

  const handleSubmit = async () => {
    if (!validateWelderReportForm(wiobj, ['project', 'slipStatus', 'fileFormat'])) {
      return;
    }

    setLoading(true);
    try {

      // Performance monitoring for large datasets
      const startTime = Date.now();
      const transformedData = await fetchAndProcessData();

      if (transformedData?.length === 0) {
        showInfo("There are no records matching to filters.");
        return;
      }

      // Memory usage warning for large datasets
      if (transformedData.length > 10000) {
        console.warn(`⚠️ Large export detected: ${transformedData.length} records. This may take several minutes.`);
      }

      console.log(`📊 Export data prepared:`, {

        wpsEntries: transformedData.length,
        format: wiobj.WI_FileFormat
      });

      if (wiobj?.WI_FileFormat === "Excel") {
        await exportToExcel(transformedData);
      } else if (wiobj?.WI_FileFormat === "PDF") {
        await exportToPDF(transformedData);
      }

      const totalTime = Date.now() - startTime;
      console.log(`✅ Export completed in ${totalTime}ms (${Math.round(totalTime / 1000)}s)`);

      showSuccess(`Exported successfully! (${transformedData.length} records in ${Math.round(totalTime / 1000)}s)`);

    } catch (error) {
      console.error('❌ Error in handleSubmit:', error);

      // Enhanced error handling for large datasets
      if (error.message?.includes('memory') || error.message?.includes('heap')) {
        showWarning('Export failed due to large dataset size. Please try filtering to reduce the number of records.');
      } else if (error.message?.includes('timeout')) {
        showWarning('Export timed out. Please try with a smaller date range or specific project/joint filters.');
      } else {
        showWarning('An error occurred while processing the request. Please try again with more specific filters.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <GridLoader size={10} className="pageloader" />
        </div>
      )}
      <Container fluid>
        <Row className="RowHeight mb-4 ml-2">
          <Col md={12} sm={12} xs={12} className="ListTitle">
            Welder Record Sheet Used in Project Filters
          </Col>
        </Row>
        <WelderReportFilters
          wiobj={wiobj}
          setwiobj={setwiobj}
          error={error}
          setError={setError}
          uniqueProjectNumbers={getUniqueProjectNumbers()}
          jointOptions={getJointNosForSelectedProject()}
          welderOptions={getWelderForSelectedProject()}
          slipStatusOptions={Slipstatus}
          onProjectChange={handleProjectChange}
          onJointChange={handleJointChange}
          onWelderChange={handleWelderChange}
          onSlipStatusChange={handleSlipStatusChange}
          showFileFormat={true}
          fileFormatOptions={fileformat}
        />
        <Row>
          <Col>
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="btn-add-color semiBoldToggleButton rightAlignedButton"
            >
              {loading ? 'Processing...' : 'Download'}
            </Button>
          </Col>
        </Row>
      </Container>
    </>
  );
});

export default Welder_Record_sheet;
